import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { Feature } from 'geojson'
import { useEffect } from 'react'
import { useControl } from 'react-map-gl'
import type { ControlPosition, MapRef } from 'react-map-gl'
import { MISSION_MAP_DRAWING_MODES } from 'utils/constants'
import { ILaunchPoint, IPoint, ITransferRoute, IZone } from 'utils/interfaces'
import { IRoute } from 'utils/interfaces'

import DirectSelectMissionPoint from './modes/DirectSelectMissionPoint'
import DirectSelectTransferRouteMode from './modes/DirectSelectTransferRouteMode'
import DirectSelectZoneMode from './modes/DirectSelectZoneMode'
import DirectSelectZoneRouteMode from './modes/DirectSelectZoneRouteMode'
import DrawPolygonMode from './modes/DrawPolygonMode'
import DrawRouteMode from './modes/DrawRouteMode'
import DrawPointMode from './modes/LaunchPointMode'
import { ViewZonesDrawMode } from './modes/ViewZonesDrawMode'

type DrawControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
  position?: ControlPosition
  mode: MISSION_MAP_DRAWING_MODES
  tempZone: IZone | undefined
  tempZoneRoute: IRoute | undefined
  tempLaunchPoint: ILaunchPoint | undefined
  tempTransfer: ITransferRoute | undefined
  tempMissionPoint: IPoint | undefined
  onCreate: (evt: { features: Feature[] }) => void
  onUpdate: (evt: { features: Feature[]; action: string }) => void
  onDelete: (evt: { features: Feature[] }) => void
  setEditingTempLaunchPoint: () => void
  setEditingTempZone: () => void
  setEditingTempZoneRoute: () => void
}

export default function DrawControl(props: DrawControlProps) {
  const control = useControl<MapboxDraw>(
    () =>
      new MapboxDraw({
        ...props,
        defaultMode: 'view_zones',
        modes: Object.assign(
          {
            view_zones: ViewZonesDrawMode,
            draw_polygon: DrawPolygonMode,
            draw_route: DrawRouteMode,
            direct_select_zone: DirectSelectZoneMode,
            draw_point: DrawPointMode,
            direct_select_transfer_route: DirectSelectTransferRouteMode,
            direct_select_zone_route: DirectSelectZoneRouteMode,
            direct_select_mission_point: DirectSelectMissionPoint,
          },
          MapboxDraw.modes
        ),
      }),
    ({ map }: { map: MapRef }) => {
      map.on('draw.create', props.onCreate)
      map.on('draw.update', props.onUpdate)
      map.on('draw.delete', props.onDelete)

      const mapInstance = map.getMap()
      if (mapInstance) {
        if (!(mapInstance as any)._originalAddLayer) {
          ;(mapInstance as any)._originalAddLayer = mapInstance.addLayer.bind(mapInstance)
        }

        mapInstance.addLayer = function (layer: any, beforeId?: string) {
          try {
            if (mapInstance.getLayer(layer.id)) {
              return mapInstance
            }
          } catch (err) {}
          return (mapInstance as any)._originalAddLayer(layer, beforeId)
        }
      }

      return () => {
        if (mapInstance && (mapInstance as any)._originalAddLayer) {
          mapInstance.addLayer = (mapInstance as any)._originalAddLayer
          delete (mapInstance as any)._originalAddLayer
        }
      }
    },
    ({ map }: { map: MapRef }) => {
      map.off('draw.create', props.onCreate)
      map.off('draw.update', props.onUpdate)
      map.off('draw.delete', props.onDelete)
    },
    {
      position: props.position,
    }
  ) as MapboxDraw

  useEffect(() => {
    if (props.mode != MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE) {
      if (props.tempZone)
        if (!control.get(String(props.tempZone?.id)) && !props.tempZone.properties?.filename) {
          // the zone editing mode
          control.add(props.tempZone)
        }
    }

    if (props.tempZoneRoute) {
      if (!control.get(String(props.tempZoneRoute?.id))) {
        control.add(props.tempZoneRoute)
      }
    }

    if (props.tempLaunchPoint) {
      if (!control.get(String(props.tempLaunchPoint?.id))) {
        control.add(props.tempLaunchPoint)
      }
    } else {
      control.getAll().features.forEach(feature => {
        const featureInProps =
          (props.tempLaunchPoint?.id && String(props.tempLaunchPoint.id) === String(feature.id)) ||
          (props.tempZone?.id && String(props.tempZone.id) === String(feature.id)) ||
          (props.tempTransfer?.id && String(props.tempTransfer.id) === String(feature.id)) ||
          (props.tempZoneRoute?.id && String(props.tempZoneRoute.id) === String(feature.id))
        if (!featureInProps) {
          control.delete(String(feature.id))
        }
      })
    }

    if (props.tempTransfer) {
      if (!control.get(String(props.tempTransfer?.id))) {
        control.add(props.tempTransfer)
      }
    }

    if (props.tempMissionPoint) {
      if (!control.get(String(props.tempMissionPoint?.id))) {
        control.add(props.tempMissionPoint)
      }
    }
  }, [control, props.tempLaunchPoint, props.tempMissionPoint, props.tempTransfer, props.tempZone, props.tempZoneRoute])

  useEffect(() => {
    const mapInstance = (control as any)._map
    if (!mapInstance) return

    // Remove all listeners first
    mapInstance.off('click', 'gl-draw-launch-point.cold', props.setEditingTempLaunchPoint as any)
    mapInstance.off('click', 'gl-draw-launch-point-center.cold', props.setEditingTempLaunchPoint as any)
    mapInstance.off('click', 'gl-draw-polygon-fill.cold', props.setEditingTempZone as any)
    mapInstance.off('click', 'gl-draw-line-zone-route.cold', props.setEditingTempZoneRoute as any)

    if (props.mode !== MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE) {
      mapInstance.on('click', 'gl-draw-launch-point.cold', props.setEditingTempLaunchPoint as any)
      mapInstance.on('click', 'gl-draw-launch-point-center.cold', props.setEditingTempLaunchPoint as any)
      mapInstance.on('click', 'gl-draw-polygon-fill.cold', props.setEditingTempZone as any)
    }

    return () => {
      if (mapInstance) {
        mapInstance.off('click', 'gl-draw-launch-point.cold', props.setEditingTempLaunchPoint as any)
        mapInstance.off('click', 'gl-draw-launch-point-center.cold', props.setEditingTempLaunchPoint as any)
        mapInstance.off('click', 'gl-draw-polygon-fill.cold', props.setEditingTempZone as any)
        mapInstance.off('click', 'gl-draw-line-zone-route.cold', props.setEditingTempZoneRoute as any)
      }
    }
  }, [control, props.mode, props.setEditingTempLaunchPoint, props.setEditingTempZone, props.setEditingTempZoneRoute])

  useEffect(() => {
    switch (props.mode) {
      case MISSION_MAP_DRAWING_MODES.VIEW_ZONES:
        control.deleteAll()
        control.changeMode('view_zones')
        break
      case MISSION_MAP_DRAWING_MODES.DRAW_POLYGON:
        control.changeMode('draw_polygon')
        break
      case MISSION_MAP_DRAWING_MODES.DRAW_ROUTE:
        control.changeMode('draw_route')
        break
      case MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE:
        if (props.tempZone && props.tempZone.id) {
          control.changeMode('direct_select_zone', { featureId: String(props.tempZone.id) })
        }
        break
      case MISSION_MAP_DRAWING_MODES.ADD_ZONE_LAUNCH_POINT:
        control.changeMode('draw_point')
        break
      case MISSION_MAP_DRAWING_MODES.ADD_MISSION_START_POINT:
      case MISSION_MAP_DRAWING_MODES.ADD_MISSION_FINISH_POINT:
        control.changeMode('draw_point')
        break
      case MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_TRANSFER_ROUTE:
        if (props.tempTransfer && props.tempTransfer.id) {
          control.changeMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_TRANSFER_ROUTE, {
            featureId: String(props.tempTransfer.id),
          })
        }
        break
      case MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE:
        if (props.tempZoneRoute && props.tempZoneRoute.id) {
          const coordPath = props.tempZoneRoute.geometry.type === 'MultiLineString' ? '0.0' : '0'
          control.changeMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE, {
            featureId: String(props.tempZoneRoute.id),
          })
        }
        break
      case MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_MISSION_POINT:
        if (props.tempMissionPoint && props.tempMissionPoint.id) {
          control.changeMode('direct_select_mission_point', {
            featureIds: [String(props.tempMissionPoint.id)],
          })
        }
        break
      case MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_LAUNCH_POINT:
        if (props.tempLaunchPoint && props.tempLaunchPoint.id) {
          control.changeMode('direct_select_mission_point', {
            featureIds: [String(props.tempLaunchPoint.id)],
          })
        }
        break
      default:
        control.changeMode('view_zones')
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode])

  return null
}
